import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import type { Language, Translations } from './translations';
import { frTranslations, enTranslations } from './translations';

// Create the context
interface TranslationContextType {
  language: Language;
  t: Translations;
  changeLanguage: (lang: Language) => void;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Provider component
interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('fr');
  
  // Get translations based on the selected language
  const getTranslations = (): Translations => {
    return language === 'fr' ? frTranslations : enTranslations;
  };
  
  // Change language
  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    // Optionally save to localStorage for persistence
    localStorage.setItem('language', lang);
  };
  
  // Get translations on initial load
  React.useEffect(() => {
    // Check if language preference is stored in localStorage
    const savedLanguage = localStorage.getItem('language') as Language | null;
    if (savedLanguage && (savedLanguage === 'fr' || savedLanguage === 'en')) {
      setLanguage(savedLanguage);
    }
  }, []);
  
  const value = {
    language,
    t: getTranslations(),
    changeLanguage,
  };
  
  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook to use the translation context
export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
